import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
	SipAccountOrderRequest,
	SipAccountOrderResponse,
	SipOrderTarget,
	toStartDate,
} from '../models/sip-account-order.model';
import { LinkOrderRequest } from '../models/sip-order-link.model';

/**
 * PS-6495 — orders a SIP-Account for the customer's phone service.
 *
 * This is the one call in the Connect page that concludes a paid contract, and the only one
 * carrying the customer's access token. Two properties are load-bearing and must not be
 * "simplified" away:
 *
 * - **No `uid`.** The token authorises this call; the `uid` authorises reading the callnote and
 *   creating the task. Mixing the two would make a forwarded email link sufficient to order a
 *   product.
 * - **`phoneServiceId` comes from the token**, resolved via `/oauth/check_token`. This service
 *   takes it as an argument and never looks it up, so the value booked against is always the one
 *   the token resolved.
 *
 *   Before it gets here, `GuidedSetup.resolvePhoneService` has compared it with the callnote's
 *   `phoneServiceId` (PS-6646) and refused the sign-in on a mismatch: opening customer A's link and
 *   signing in as B used to book on B's contract while the callback task went to A's `uid`. That
 *   check is a client-side guard against mix-ups, NOT authorization — bookingmgmt does not see the
 *   callnote, and its ACL only proves the token owns the phone service it books on, which a token
 *   for B legitimately does (PS-6646, D4).
 *
 * A SIP order link orders via `SipOrderTarget` kind `link`: same response and timeout, other URL and body.
 *
 * The endpoint is idempotent: if a `CALLCENTER` number on the `OUTBOX` carrier already exists for
 * the phone service, it is returned and nothing is booked. It is also **not** reversible — a
 * wrongly booked number is billed and needs a manual cancellation — so callers must not retry it
 * blindly on timeout.
 */
/**
 * How long the order may take before its outcome counts as UNKNOWN (PS-6495 M4). Deliberately
 * generous: the call concludes a paid contract, and a timeout does not mean nothing was booked -
 * the request may still complete on the server. The caller therefore surfaces a timeout (and an
 * HTTP 504) as "unconfirmed", never as a refusal, and never retries.
 */
export const SIP_ORDER_TIMEOUT_MS = 60_000;

@Injectable({
	providedIn: 'root',
})
export class SipAccountOrderService {
	private readonly httpClient: HttpClient = inject(HttpClient);

	/** Orders the SIP-Account for `target`; the only place the two entry points differ. */
	orderSipAccount$(target: SipOrderTarget, startDate: Date): Observable<SipAccountOrderResponse> {
		const start = toStartDate(startDate);
		const request$ =
			target.kind === 'link'
				? this.httpClient.post<SipAccountOrderResponse>(
						`${environment.baseUrl}/bookingmgmt/v2/outbox/linkorders`,
						{ token: target.token, startDate: start } satisfies LinkOrderRequest,
					)
				: this.httpClient.post<SipAccountOrderResponse>(
						`${environment.baseUrl}/bookingmgmt/v2/outbox`,
						{ phoneServiceId: target.phoneServiceId, startDate: start } satisfies SipAccountOrderRequest,
					);
		return request$.pipe(timeout(SIP_ORDER_TIMEOUT_MS));
	}
}
